import React, { useEffect, useRef, useState } from "react";
import { Terminal as XTerm } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";

// Real PTY shell on the master server, attached over WebSocket.
// Backend: /api/terminal?node=<name>  (flask-sock; see monitor.py)
// "master" or omitted → ssh to the cluster head node (10.128.97.9). Other names → ssh user@node.
//
// The pfm_session httponly cookie is sent automatically with the WS handshake.
//
// Remounts when `node` changes (key={node} from parent), so we never reuse a
// closed WebSocket or a PTY bound to the wrong host.
export default function Terminal({ node = "master" }) {
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const wsRef = useRef(null);
  const [status, setStatus] = useState("connecting"); // connecting | open | closed | error

  useEffect(() => {
    const term = new XTerm({
      cursorBlink: true,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      fontSize: 13,
      theme: {
        background: "#0b1020",
        foreground: "#e5e7eb",
        cursor: "#3b82f6",
      },
      scrollback: 5000,
      convertEol: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    try { fit.fit(); } catch { /* container may not be measured yet */ }
    termRef.current = term;
    term.focus();

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/api/terminal?node=${encodeURIComponent(node)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("open");
      ws.send(JSON.stringify({ type: "resize", rows: term.rows, cols: term.cols }));
      term.focus();
    };
    ws.onmessage = (ev) => term.write(ev.data);
    ws.onerror = () => {
      setStatus("error");
      term.writeln("\r\n\x1b[31m[connection error]\x1b[0m");
    };
    ws.onclose = () => {
      setStatus("closed");
      term.writeln("\r\n\x1b[33m[session closed]\x1b[0m");
    };

    const dataDisp = term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(data);
    });

    const handleResize = () => {
      try { fit.fit(); } catch { return; }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "resize", rows: term.rows, cols: term.cols }));
      }
    };
    window.addEventListener("resize", handleResize);
    const ro = new ResizeObserver(handleResize);
    ro.observe(containerRef.current);

    return () => {
      window.removeEventListener("resize", handleResize);
      ro.disconnect();
      dataDisp.dispose();
      try { ws.close(); } catch { /* ignore */ }
      term.dispose();
    };
  }, [node]);

  // Clicking anywhere in the wrapper drops focus onto xterm's hidden textarea.
  // Without this, clicks on the padded area around the canvas don't focus.
  const focusTerm = () => {
    try { termRef.current && termRef.current.focus(); } catch { /* ignore */ }
  };

  const statusColor =
    status === "open" ? "#22c55e" :
    status === "connecting" ? "#facc15" :
    "#ef4444";

  return (
    <div
      onMouseDown={focusTerm}
      style={{
        display: "flex", flexDirection: "column", height: "100%",
        backgroundColor: "#0b1020", borderRadius: 8, overflow: "hidden",
        border: "1px solid #1f2937",
      }}
    >
      <div style={{
        padding: "6px 14px", fontSize: 12, color: "#9ca3af",
        borderBottom: "1px solid #1f2937", backgroundColor: "#0d1424",
        fontFamily: "monospace",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%",
          backgroundColor: statusColor, display: "inline-block",
        }} />
        <span style={{ color: "#e5e7eb" }}>
          {`ssh ${node}`}
        </span>
        <span style={{ marginLeft: "auto" }}>{status}</span>
      </div>
      <div
        ref={containerRef}
        onClick={focusTerm}
        style={{ flex: 1, padding: 8, minHeight: 0, cursor: "text" }}
      />
    </div>
  );
}
